/**
 * MainChat Component
 * Shows all messages across all projects with relevant tasks
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MessageCircle, Image, FileText, ClipboardList } from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { API, getApiUrl } from '../../utils/api';

const MainChat = ({ currentUser, onMessageClick, onBackToProjects, onFilePreview, onTaskClick }) => {
  const { t } = useTranslation();
  const [allMessages, setAllMessages] = useState([]);
  const [relevantTasks, setRelevantTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }
    
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const [messagesRes, tasksRes] = await Promise.all([
          axios.get(`${getApiUrl()}/users/${currentUser.id}/messages`, {
            params: { user_language: currentUser?.language || 'de' }
          }),
          axios.get(`${getApiUrl()}/tasks/user/${currentUser.id}/relevant`)
        ]);
        
        if (isMounted) {
          setAllMessages(messagesRes.data);
          setRelevantTasks(tasksRes.data.tasks || []);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [currentUser?.id]);

  const formatMessageTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return t('time.now', 'Now');
      if (diffMins < 60) return `${diffMins} ${t('time.min', 'min')}`;
      if (diffHours < 24) return `${diffHours} ${t('time.hours', 'h')}`;
      if (diffDays < 7) return `${diffDays} ${t('time.days', 'd')}`;
      return date.toLocaleDateString(currentUser?.language || 'de');
    } catch (e) {
      return '';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getMessageIcon = (msg) => {
    if (msg.has_image || msg.file_type === 'image') {
      return <Image className="w-4 h-4 text-blue-500" />;
    }
    if (msg.file_path || msg.file_type) {
      return <FileText className="w-4 h-4 text-orange-500" />;
    }
    return <MessageCircle className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2"></div>
          <p className="text-gray-600">{t('chat.loadingMessages', 'Lade Nachrichten...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Relevant Tasks Section */}
      {relevantTasks.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 p-3">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">{t('chat.openTasksForYou', 'Offene Tasks für Sie')} ({relevantTasks.length})</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {relevantTasks.slice(0, 5).map(task => (
              <div 
                key={task.id}
                onClick={() => onTaskClick && onTaskClick(task)}
                className="flex-shrink-0 bg-white rounded-lg px-3 py-2 shadow-sm border border-amber-200 cursor-pointer hover:bg-amber-50 transition-colors max-w-[200px]"
              >
                <p className="text-sm font-medium text-gray-800 truncate">{task.title}</p>
                <p className="text-xs text-gray-500 truncate">{task.project_name}</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs">
                    {task.status === 'open' ? '📋' : task.status === 'review' ? '👁️' : '🔧'}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    task.priority === 'high' ? 'bg-red-100 text-red-700' :
                    task.priority === 'low' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {task.priority === 'high' ? t('tasks.high', 'Hoch') : task.priority === 'low' ? t('tasks.low', 'Niedrig') : t('tasks.medium', 'Normal')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages List */}
      <div className="flex-1 overflow-y-auto">
        {allMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>{t('chat.noMessages', 'Noch keine Nachrichten')}</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {allMessages.map((msg, index) => (
              <div 
                key={msg.id || index}
                onClick={() => onMessageClick && onMessageClick(msg)}
                className="flex items-start gap-3 p-4 hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <Avatar className="w-10 h-10 flex-shrink-0">
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm">
                    {getInitials(msg.sender_name)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-900 truncate">{msg.sender_name}</span>
                    <span className="text-xs text-gray-500 flex-shrink-0">{formatMessageTime(msg.timestamp)}</span>
                  </div>
                  <p className="text-sm text-emerald-600 truncate">{msg.project_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getMessageIcon(msg)}
                    <p className="text-sm text-gray-600 truncate">
                      {msg.translated_text || msg.original_text || msg.text || '[Datei]'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MainChat;
